import { CAD_STOCK_LABELS } from '../../Showroom/data/cadConstants';
import { OPERATION_LABEL } from './listingCoreConstants';

const ACTION_LABEL = {
  created: 'Creó la lista',
  renamed: 'Cambió el nombre',
  shared: 'Compartió la lista',
  unshared: 'Dejó de compartir',
  share_updated: 'Actualizó el acceso',
  member_added: 'Agregó un editor',
  member_removed: 'Quitó un editor',
  item_updated: 'Actualizó un lote',
};

const FIELD_LABEL = {
  title: 'Título',
  description: 'Descripción',
  price: 'Precio',
  currency: 'Moneda',
  operation: 'Operación',
  stock_status: 'Estado',
};

const CHANGE_SUMMARY = {
  title: 'Cambió el título',
  description: 'Cambió la descripción',
  price: 'Cambió el precio',
  currency: 'Cambió la moneda',
  operation: 'Cambió la operación',
  stock_status: 'Cambió el estado',
};

const ROLE_LABEL = {
  owner: 'Dueño',
  editor: 'Editor',
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Períodos del historial. El GET no filtra: se aplica en el cliente. */
export const ACTIVITY_TIME_RANGE_OPTIONS = [
  { id: '7d', label: 'Últimos 7 días', days: 7 },
  { id: '30d', label: 'Últimos 30 días', days: 30 },
  { id: 'all', label: 'Todo el tiempo', days: null },
];

/** Quién hizo la acción. */
export const ACTIVITY_ROLE_FILTER_OPTIONS = [
  { id: 'all', label: 'Todos' },
  { id: 'owner', label: 'Dueño' },
  { id: 'editor', label: 'Editor' },
];

/**
 * @param {string} action
 * @returns {string}
 */
export function getCollectionActivityActionLabel(action) {
  const key = String(action || '').trim();
  return ACTION_LABEL[key] || 'Actividad';
}

/**
 * @param {string} field
 * @returns {string}
 */
export function getCollectionActivityFieldLabel(field) {
  const key = String(field || '').trim();
  return FIELD_LABEL[key] || key || 'Campo';
}

/**
 * @param {string} field
 * @returns {string}
 */
export function getCollectionActivityChangeSummary(field) {
  const key = String(field || '').trim();
  if (CHANGE_SUMMARY[key]) return CHANGE_SUMMARY[key];
  const label = getCollectionActivityFieldLabel(key);
  return label ? `Cambió ${label.toLowerCase()}` : 'Cambió un campo';
}

/**
 * @param {string} role
 * @returns {string}
 */
export function getCollectionActivityRoleLabel(role) {
  const key = String(role || '').trim();
  return ROLE_LABEL[key] || '';
}

function normalizeSearchText(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/**
 * @param {string} value
 * @returns {string}
 */
export function formatCollectionActivityTime(value) {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '';
  return date.toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function startOfLocalDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

/**
 * Reloj corto: hoy → `13:27`; otro día del año → `07/09 · 13:27`.
 *
 * @param {string} value
 * @param {Date} [now]
 * @returns {string}
 */
export function formatCollectionActivityClock(value, now = new Date()) {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime()) || !Number.isFinite(now.getTime())) return '';
  const time = date.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
  if (startOfLocalDay(date) === startOfLocalDay(now)) return time;
  const day = `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}`;
  if (date.getFullYear() === now.getFullYear()) return `${day} · ${time}`;
  return `${day}/${date.getFullYear()} · ${time}`;
}

/**
 * @param {string} value
 * @returns {string}
 */
export function getActivityDayKey(value) {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return 'unknown';
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/**
 * @param {string} value
 * @param {Date} [now]
 * @returns {string}
 */
export function formatActivityDayLabel(value, now = new Date()) {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime()) || !Number.isFinite(now.getTime())) return 'Fecha desconocida';
  const diffDays = Math.round((startOfLocalDay(now) - startOfLocalDay(date)) / MS_PER_DAY);
  if (diffDays === 0) return 'Hoy';
  if (diffDays === 1) return 'Ayer';
  return date.toLocaleDateString('es-AR', {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
  });
}

/**
 * @param {object} row
 * @param {string} rangeId
 * @param {number} [nowMs]
 * @returns {boolean}
 */
export function activityMatchesTimeRange(row, rangeId, nowMs = Date.now()) {
  const option = ACTIVITY_TIME_RANGE_OPTIONS.find((item) => item.id === rangeId);
  if (!option || option.days == null) return true;
  const created = new Date(row?.created_at).getTime();
  if (!Number.isFinite(created)) return false;
  return created >= nowMs - option.days * MS_PER_DAY;
}

/**
 * Texto buscable de una fila: título, actor, lote, emails y cambios.
 *
 * @param {object} row
 * @returns {string}
 */
export function getCollectionActivitySearchText(row) {
  const display = formatCollectionActivityRow(row);
  return normalizeSearchText([
    display.title,
    display.actor,
    display.roleLabel,
    getCollectionActivityTargetEmail(row),
    getCollectionActivityItemLabel(row),
    ...display.changes.flatMap((change) => [
      change.field,
      change.summary,
      change.from,
      change.to,
    ]),
  ].filter(Boolean).join(' '));
}

/**
 * @param {object[]} rows
 * @param {{ query?: string, timeRange?: string, role?: string, now?: number }} [filters]
 * @returns {object[]}
 */
export function filterCollectionActivityRows(rows, filters = {}) {
  const query = normalizeSearchText(filters.query);
  const timeRange = filters.timeRange || 'all';
  const role = filters.role || 'all';
  const now = filters.now ?? Date.now();
  return (Array.isArray(rows) ? rows : []).filter((row) => {
    if (role !== 'all' && String(row?.actor_role || '').trim() !== role) {
      return false;
    }
    if (query && !getCollectionActivitySearchText(row).includes(query)) {
      return false;
    }
    return activityMatchesTimeRange(row, timeRange, now);
  });
}

/**
 * @param {object[]} rows
 * @param {Date} [now]
 * @returns {{ key: string, label: string, rows: object[] }[]}
 */
export function groupCollectionActivityByDay(rows, now = new Date()) {
  const groups = [];
  const indexByKey = new Map();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const key = getActivityDayKey(row?.created_at);
    let group = indexByKey.get(key);
    if (!group) {
      group = {
        key,
        label: formatActivityDayLabel(row?.created_at, now),
        rows: [],
      };
      indexByKey.set(key, group);
      groups.push(group);
    }
    group.rows.push(row);
  });
  return groups;
}

function formatChangeValue(field, value) {
  if (value == null || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'sí' : 'no';
  const key = String(value).trim();
  const lookup = key.toLowerCase();
  if (field === 'stock_status' && CAD_STOCK_LABELS[lookup]) {
    return CAD_STOCK_LABELS[lookup];
  }
  if (field === 'operation' && OPERATION_LABEL[lookup]) {
    return OPERATION_LABEL[lookup];
  }
  return key;
}

function readEmail(value) {
  return String(value || '').trim();
}

function readItemText(value) {
  return String(value || '').trim();
}

/**
 * Ficha hidratada del GET. Si el lote/UF ya no existe, `item` puede ser null
 * y quedar un snapshot en el payload.
 *
 * @param {object} row
 * @returns {object|null}
 */
export function getCollectionActivityItem(row) {
  if (row?.item && typeof row.item === 'object') return row.item;
  const payload = row?.payload && typeof row.payload === 'object' ? row.payload : {};
  if (payload.item && typeof payload.item === 'object') return payload.item;
  if (payload.snapshot && typeof payload.snapshot === 'object') return payload.snapshot;
  return null;
}

/**
 * Título de la ficha: title → unit_label → name.
 *
 * @param {object} row
 * @returns {string}
 */
export function getCollectionActivityItemLabel(row) {
  const item = getCollectionActivityItem(row);
  if (!item) return '';
  return readItemText(item.title) || readItemText(item.unit_label) || readItemText(item.name);
}

/**
 * Destino para abrir la ficha. Solo si `item` vive todavía (no snapshot).
 *
 * @param {object} row
 * @returns {{ itemType: string, lotId?: string, userLotId?: string, parcelId?: string, label: string }|null}
 */
export function getCollectionActivityOpenTarget(row) {
  if (String(row?.action || '').trim() !== 'item_updated') return null;
  const item = row?.item && typeof row.item === 'object' ? row.item : null;
  if (!item) return null;
  const itemType = String(item.item_type || '').trim();
  const label = getCollectionActivityItemLabel(row);
  if (itemType === 'unit') {
    const userLotId = readItemText(item.user_lot_id);
    if (!userLotId) return null;
    return {
      itemType,
      lotId: readItemText(item.lot_id),
      userLotId,
      label,
    };
  }
  if (itemType === 'lot') {
    const lotId = readItemText(item.lot_id);
    if (!lotId) return null;
    return { itemType, lotId, userLotId: null, label };
  }
  if (itemType === 'custom_parcel') {
    const parcelId = readItemText(item.id) || readItemText(row?.entity_id);
    if (!parcelId) return null;
    return { itemType, parcelId, label };
  }
  return null;
}

/**
 * Email de la persona agregada o quitada, si el API lo mandó en el payload.
 *
 * @param {object} row
 * @returns {string}
 */
export function getCollectionActivityTargetEmail(row) {
  const payload = row?.payload && typeof row.payload === 'object' ? row.payload : {};
  return (
    readEmail(payload.email)
    || readEmail(payload.member_email)
    || readEmail(payload.target_email)
    || readEmail(payload.invited_email)
    || readEmail(payload.member?.email)
    || readEmail(row?.entity_email)
  );
}

/**
 * @param {object} row
 * @returns {string}
 */
export function getCollectionActivityTitle(row) {
  const action = String(row?.action || '').trim();
  const target = getCollectionActivityTargetEmail(row);
  if (action === 'member_added') {
    return target ? `Agregó a ${target}` : 'Agregó un editor';
  }
  if (action === 'member_removed') {
    return target ? `Quitó a ${target}` : 'Quitó un editor';
  }
  if (action === 'item_updated') {
    const itemLabel = getCollectionActivityItemLabel(row);
    return itemLabel ? `Actualizó ${itemLabel}` : 'Actualizó un lote';
  }
  return getCollectionActivityActionLabel(action);
}

/**
 * @param {object} row
 * @returns {{
 *   title: string,
 *   subtitle: string,
 *   actor: string,
 *   roleKey: string,
 *   roleLabel: string,
 *   time: string,
 *   timeShort: string,
 *   changes: { field: string, summary: string, from: string, to: string }[],
 * }}
 */
export function formatCollectionActivityRow(row) {
  const actor = readEmail(row?.actor_email);
  const roleKey = String(row?.actor_role || '').trim();
  const roleLabel = getCollectionActivityRoleLabel(roleKey);
  const title = getCollectionActivityTitle(row);
  const subtitle = [actor, roleLabel].filter(Boolean).join(' · ');
  const changes = Array.isArray(row?.payload?.changes)
    ? row.payload.changes
      .filter((item) => item && (item.field || item.old != null || item.new != null))
      .map((item) => ({
        field: getCollectionActivityFieldLabel(item.field),
        summary: getCollectionActivityChangeSummary(item.field),
        from: formatChangeValue(item.field, item.old),
        to: formatChangeValue(item.field, item.new),
      }))
    : [];
  return {
    title,
    subtitle,
    actor,
    roleKey,
    roleLabel,
    time: formatCollectionActivityTime(row?.created_at),
    timeShort: formatCollectionActivityClock(row?.created_at),
    changes,
  };
}
